import React, { useState, useEffect } from 'react';
import {
  FluentProvider,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import { AuthProvider, useAuth } from './auth';
import { LoginButton, AreaSelector, ChatInterface, AppLayout } from './components';
import { configService, ViewMode } from './services/configService';
import { setAccessTokenProvider } from './services/customPromptsService';
import { AreaConfig } from './types';
import { dynamics365LightTheme } from './theme';

const useStyles = makeStyles({
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#f2f2f2',
  },
});

type AppState = 'login' | 'area-selection' | 'chat';

const AppContent: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, user, logout, getAccessToken, inProgress } = useAuth();
  
  const [appState, setAppState] = useState<AppState>('login');
  const [areas, setAreas] = useState<AreaConfig[]>([]);
  const [selectedArea, setSelectedArea] = useState<AreaConfig | null>(null);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(configService.getViewMode());

  // Set up access token provider for custom prompts service
  useEffect(() => {
    if (isAuthenticated && getAccessToken) {
      setAccessTokenProvider(getAccessToken);
    }
  }, [isAuthenticated, getAccessToken]);

  // Load areas when authenticated or view mode changes
  useEffect(() => {
    const loadAreas = async () => {
      if (isAuthenticated) {
        setIsLoadingAreas(true);
        try {
          // Load areas from the JSON configuration file
          const areasData = await configService.getAreas();
          setAreas(areasData);
          setAppState('area-selection');
        } catch (error) {
          console.error('Failed to load areas:', error);
        } finally {
          setIsLoadingAreas(false);
        }
      } else {
        setAppState('login');
      }
    };

    if (!inProgress) {
      loadAreas();
    }
  }, [isAuthenticated, inProgress, viewMode]);

  const handleViewModeChange = async (mode: ViewMode) => {
    if (mode !== viewMode) {
      setIsLoadingAreas(true);
      try {
        await configService.setViewMode(mode);
        setViewMode(mode);
        const areasData = await configService.getAreas();
        setAreas(areasData);
      } catch (error) {
        console.error('Failed to switch view mode:', error);
      } finally {
        setIsLoadingAreas(false);
      }
    }
  };

  const handleSelectArea = (area: AreaConfig) => {
    setSelectedArea(area);
    setAppState('chat');
  };

  const handleBackToAreas = () => {
    setSelectedArea(null);
    setAppState('area-selection');
  };

  // Only show full loading screen during initial auth, not during view mode switch
  if (inProgress) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  // Show login page without layout
  if (appState === 'login') {
    return <LoginButton />;
  }

  // Show authenticated pages with layout
  return (
    <AppLayout
      userName={user?.name}
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
      onLogout={logout}
      currentPage={appState === 'area-selection' ? 'home' : 'chat'}
      onNavigateHome={handleBackToAreas}
      areaName={selectedArea?.name}
    >
      {appState === 'area-selection' && (
        <AreaSelector
          areas={areas}
          onSelectArea={handleSelectArea}
          userName={user?.name?.split(' ')[0]}
          isLoading={isLoadingAreas}
        />
      )}
      
      {appState === 'chat' && selectedArea && (
        <ChatInterface area={selectedArea} onBack={handleBackToAreas} />
      )}
    </AppLayout>
  );
};

const App: React.FC = () => {
  return (
    <FluentProvider theme={dynamics365LightTheme}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </FluentProvider>
  );
};

export default App;
